import { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import { getAccessToken } from '../utils/auth';
import config from '../config';
import { getAllUsers, getAllReceivers, getRecentConnectionEvents, getUnknownReceivers, adoptUnknownReceiver, dismissUnknownReceiver } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import AirportFeatures from './AirportFeatures';
import CsvDebug from './CsvDebug';
import TrackAnomalies from './TrackAnomalies';
import AdminAircraftGaps from './AdminAircraftGaps';
import AdminAirlineGaps from './AdminAirlineGaps';
import AdminAirlineSuggestions from './AdminAirlineSuggestions';
import AdminTypeIcons from './AdminTypeIcons';
import AdminReferenceData from './AdminReferenceData';

// Display the backend's connectionStatus string as "Online / Connected /
// Offline" to match the labels used on the Receivers / Live pages. The CSS
// class still derives from the underlying value (.active / .connected /
// .inactive) — only the visible label changes.
// The monitor is served by the backend (another origin), so it cannot see this login. Hand it the
// admin token in the URL fragment, which the browser never sends to the server; the page saves it
// and strips it from the address bar. The monitor sits at the web root, beside /api, not under it.
function openFrameworkMonitor() {
  const token = getAccessToken();
  const webRoot = config.AVIOTRACK_API_URL.replace(/\/api\/?$/, '');
  const url = `${webRoot}/monitor/index.html${token ? `#token=${encodeURIComponent(token)}` : ''}`;
  window.open(url, '_blank', 'noopener');
}

function receiverStatusLabel(connectionStatus) {
  if (connectionStatus === 'Active') return 'Online';
  if (connectionStatus === 'Inactive' || !connectionStatus) return 'Offline';
  return connectionStatus;
}

function Admin({ onViewLive, onNavigate, connectionEvents = [], showDebugFooter = false, onToggleDebug, userRoles = [] }) {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [initialEvents, setInitialEvents] = useState([]);
  const [unknownReceivers, setUnknownReceivers] = useState([]);
  const [unknownActiveCount, setUnknownActiveCount] = useState(0);
  const [adoptingId, setAdoptingId] = useState(null);
  const [adoptForm, setAdoptForm] = useState({ ownerEmail: '', receiverName: '', latitude: '', longitude: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAccessToken();

      // Load users, receivers, and recent events in parallel
      const [usersResponse, receiversResponse, eventsResponse] = await Promise.all([
        getAllUsers(token),
        getAllReceivers(token),
        getRecentConnectionEvents(token)
      ]);

      if (isApiSuccess(usersResponse)) {
        setUsers(usersResponse.users || []);
      } else {
        console.error('Failed to load users:', getFirstApiError(usersResponse));
      }

      if (isApiSuccess(receiversResponse)) {
        const list = receiversResponse.receivers || [];
        setReceivers(list);
      } else {
        console.error('Failed to load receivers:', getFirstApiError(receiversResponse));
      }

      if (isApiSuccess(eventsResponse)) {
        setInitialEvents(eventsResponse.events || []);
      } else {
        console.error('Failed to load connection events:', getFirstApiError(eventsResponse));
      }
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load unknown receivers
  const loadUnknownReceivers = useCallback(async () => {
    try {
      const token = getAccessToken();
      const response = await getUnknownReceivers(token);
      if (isApiSuccess(response)) {
        setUnknownReceivers(response.unknownReceivers || []);
        setUnknownActiveCount(response.activeCount || 0);
      } else {
        console.error('Failed to load unknown receivers:', getFirstApiError(response));
      }
    } catch (err) {
      console.error('Error loading unknown receivers:', err);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData();
    loadUnknownReceivers();
  }, [loadData, loadUnknownReceivers]);

  // Auto-refresh unknown receivers every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadUnknownReceivers, 30000);
    return () => clearInterval(interval);
  }, [loadUnknownReceivers]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format date with time helper
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Format time helper for event log
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Get event type display label
  const getEventLabel = (eventType) => {
    switch (eventType) {
      case 'CONNECTION_ATTEMPT': return 'Connect';
      case 'UUID_RECEIVED': return 'UUID OK';
      case 'UUID_INVALID': return 'UUID Invalid';
      case 'FIRST_BEAST_MESSAGE': return 'First Data';
      case 'DISCONNECTED': return 'Disconnected';
      default: return eventType;
    }
  };

  // Combine initial events from HTTP with real-time events from WebSocket
  // Real-time events (connectionEvents) come first, then initial events
  const allEvents = [...connectionEvents, ...initialEvents.filter(
    ie => !connectionEvents.some(ce =>
      ce.timestamp === ie.timestamp && ce.connectionId === ie.connectionId
    )
  )].slice(0, 100);

  // Get receivers for a specific user email
  const getReceiversForUser = (email) => {
    return receivers.filter(r => r.emailAddress === email);
  };

  // Handle receiver click - navigate to map with that receiver selected
  const handleReceiverClick = (receiver) => {
    if (onViewLive) {
      onViewLive(receiver);
    }
  };

  // Toggle expanded user
  const toggleUserExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  // Handle adopt button click - expand row with form
  const handleAdoptClick = (receiver) => {
    if (adoptingId === receiver.id) {
      setAdoptingId(null);
      setAdoptForm({ ownerEmail: '', receiverName: '', latitude: '', longitude: '' });
    } else {
      setAdoptingId(receiver.id);
      setAdoptForm({
        ownerEmail: '',
        receiverName: '',
        latitude: receiver.estimatedLatitude != null ? String(receiver.estimatedLatitude) : '',
        longitude: receiver.estimatedLongitude != null ? String(receiver.estimatedLongitude) : ''
      });
    }
    setActionMessage(null);
  };

  // Submit adopt form
  const handleAdoptSubmit = async (unknownReceiverId) => {
    if (!adoptForm.ownerEmail.trim() || !adoptForm.receiverName.trim()) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const token = getAccessToken();
      const data = {
        unknownReceiverId,
        ownerEmail: adoptForm.ownerEmail.trim(),
        receiverName: adoptForm.receiverName.trim()
      };
      if (adoptForm.latitude) data.latitude = parseFloat(adoptForm.latitude);
      if (adoptForm.longitude) data.longitude = parseFloat(adoptForm.longitude);
      const response = await adoptUnknownReceiver(data, token);
      if (isApiSuccess(response)) {
        setActionMessage({ type: 'success', text: `Receiver adopted successfully. UUID: ${response.receiverUuid}` });
        setAdoptingId(null);
        setAdoptForm({ ownerEmail: '', receiverName: '', latitude: '', longitude: '' });
        loadUnknownReceivers();
        loadData();
      } else {
        setActionMessage({ type: 'error', text: getFirstApiError(response) || 'Failed to adopt receiver' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to adopt receiver. Please try again.' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle dismiss
  const handleDismiss = async (unknownReceiverId) => {
    if (!window.confirm('Block this IP from reconnecting?')) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const token = getAccessToken();
      const response = await dismissUnknownReceiver({ unknownReceiverId }, token);
      if (isApiSuccess(response)) {
        setActionMessage({ type: 'success', text: 'Receiver dismissed and IP blocked.' });
        loadUnknownReceivers();
      } else {
        setActionMessage({ type: 'error', text: getFirstApiError(response) || 'Failed to dismiss receiver' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to dismiss receiver. Please try again.' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading admin data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-error">
          <p>{error}</p>
          <button onClick={loadData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <h1>Admin Dashboard</h1>

      <div className="admin-stats">
        <div className="stat-card">
          <span className="stat-value">{users.length}</span>
          <span className="stat-label">Total Users</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{receivers.length}</span>
          <span className="stat-label">Total Receivers</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{receivers.filter(r => r.isActive).length}</span>
          <span className="stat-label">Active Receivers</span>
        </div>
        <div className="stat-card clickable" onClick={onToggleDebug}>
          <span className="stat-value">{showDebugFooter ? 'ON' : 'OFF'}</span>
          <span className="stat-label">Debug Log</span>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users ({users.length})
        </button>
        <button
          className="tab-button tab-button-link"
          onClick={() => onNavigate && onNavigate('receivers', { scope: 'all' })}
          title="Open the Receivers page in All receivers scope"
        >
          All Receivers ({receivers.length}) →
        </button>
        <button
          className={`tab-button ${activeTab === 'airports' ? 'active' : ''}`}
          onClick={() => setActiveTab('airports')}
        >
          Airport Features
        </button>
        <button
          className={`tab-button ${activeTab === 'unknown' ? 'active' : ''}`}
          onClick={() => { setActiveTab('unknown'); setActionMessage(null); }}
        >
          Unknown Receivers{unknownActiveCount > 0 && <span className="tab-badge">{unknownActiveCount}</span>}
        </button>
        <button
          className={`tab-button ${activeTab === 'anomalies' ? 'active' : ''}`}
          onClick={() => setActiveTab('anomalies')}
        >
          Track Anomalies
        </button>
        <button
          className={`tab-button ${activeTab === 'aircraftGaps' ? 'active' : ''}`}
          onClick={() => setActiveTab('aircraftGaps')}
        >
          Aircraft Gaps
        </button>
        <button
          className={`tab-button ${activeTab === 'airlineGaps' ? 'active' : ''}`}
          onClick={() => setActiveTab('airlineGaps')}
        >
          Airline Gaps
        </button>
        <button
          className={`tab-button ${activeTab === 'airlineSuggestions' ? 'active' : ''}`}
          onClick={() => setActiveTab('airlineSuggestions')}
        >
          Pending Airline Suggestions
        </button>
        <button
          className={`tab-button ${activeTab === 'typeIcons' ? 'active' : ''}`}
          onClick={() => setActiveTab('typeIcons')}
        >
          Type Icons
        </button>
        <button
          className={`tab-button ${activeTab === 'refdata' ? 'active' : ''}`}
          onClick={() => setActiveTab('refdata')}
        >
          Reference Data
        </button>
        <button
          className={`tab-button ${activeTab === 'csvdebug' ? 'active' : ''}`}
          onClick={() => setActiveTab('csvdebug')}
        >
          CSV Debug
        </button>
        {userRoles.includes('LAJES_ADMIN') && (
          <button
            className="tab-button tab-button-link"
            onClick={openFrameworkMonitor}
            title="Open the Lajes Framework monitor, signed in as you"
          >
            Framework Monitor ↗
          </button>
        )}
      </div>

      {activeTab === 'users' && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th>Email</th>
                <th>Timezone</th>
                <th>Receivers</th>
                <th>Last Login</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const userReceivers = getReceiversForUser(user.email);
                const isExpanded = expandedUser === user.id;

                return (
                  <>
                    <tr key={user.id}
                        className={`user-row ${userReceivers.length > 0 ? 'has-receivers' : ''} ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => userReceivers.length > 0 && toggleUserExpand(user.id)}>
                      <td className="expand-cell" data-label-none>
                        {userReceivers.length > 0 && (
                          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                        )}
                      </td>
                      <td data-label="Name">{user.name || '-'}</td>
                      <td data-label="Email">{user.email}</td>
                      <td data-label="Timezone">{user.timezone || '-'}</td>
                      <td data-label="Receivers">{userReceivers.length}</td>
                      <td data-label="Last Login">{formatDate(user.lastLogin)}</td>
                      <td data-label="Status">
                        <span className={`admin-status-badge ${user.active ? 'active' : 'inactive'}`}>
                          {user.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && userReceivers.length > 0 && (
                      <tr key={`${user.id}-receivers`} className="receivers-row">
                        <td colSpan="7">
                          <div className="nested-receivers">
                            <table className="nested-table">
                              <thead>
                                <tr>
                                  <th>Receiver Name</th>
                                  <th>Airport</th>
                                  <th>Location</th>
                                  <th>Status</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userReceivers.map(receiver => (
                                  <tr key={receiver.receiverUuid}>
                                    <td data-label="Receiver">{receiver.receiverName}</td>
                                    <td data-label="Airport">{receiver.airportIcao || '-'}</td>
                                    <td data-label="Location">
                                      {receiver.latitude && receiver.longitude
                                        ? `${receiver.latitude.toFixed(4)}, ${receiver.longitude.toFixed(4)}`
                                        : '-'}
                                    </td>
                                    <td data-label="Status">
                                      <span className={`admin-status-badge ${(receiver.connectionStatus || 'Inactive').toLowerCase()}`}>
                                        {receiverStatusLabel(receiver.connectionStatus)}
                                      </span>
                                    </td>
                                    <td data-label-none>
                                      <button
                                        className="view-map-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleReceiverClick(receiver);
                                        }}
                                      >
                                        View on Map
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'airports' && (
        <AirportFeatures />
      )}

      {activeTab === 'anomalies' && (
        <TrackAnomalies />
      )}

      {activeTab === 'aircraftGaps' && (
        <AdminAircraftGaps />
      )}

      {activeTab === 'airlineGaps' && (
        <AdminAirlineGaps />
      )}

      {activeTab === 'airlineSuggestions' && (
        <AdminAirlineSuggestions />
      )}

      {activeTab === 'typeIcons' && (
        <AdminTypeIcons />
      )}

      {activeTab === 'refdata' && (
        <AdminReferenceData />
      )}

      {activeTab === 'csvdebug' && (
        <CsvDebug receivers={receivers} />
      )}

      {activeTab === 'unknown' && (
        <div className="admin-table-container">
          {actionMessage && (
            <div className={`action-banner ${actionMessage.type}`}>
              {actionMessage.text}
              <button className="banner-close" onClick={() => setActionMessage(null)}>&times;</button>
            </div>
          )}
          {unknownReceivers.length === 0 ? (
            <div className="unknown-empty">No unknown receivers detected.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>IP Address</th>
                  <th>Location</th>
                  <th>Nearest Airport</th>
                  <th>Valid / Invalid</th>
                  <th>Positions</th>
                  <th>First Seen</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {unknownReceivers.map(ur => (
                  <>
                    <tr key={ur.id}>
                      <td>
                        <span className={`admin-status-badge ${ur.status === 'ACTIVE' ? 'active' : ur.status === 'DISCONNECTED' ? 'disconnected' : ur.status?.toLowerCase()}`}>
                          {ur.status}
                        </span>
                      </td>
                      <td>
                        <div>{ur.ipAddress}</div>
                        {ur.geoIsp && <div className="unknown-secondary">{ur.geoIsp}</div>}
                      </td>
                      <td>{ur.geoLocation || '-'}</td>
                      <td>{ur.nearestAirportIcao || '-'}</td>
                      <td>{(ur.validBeastMessages || 0).toLocaleString()} / {(ur.invalidMessages || 0).toLocaleString()}</td>
                      <td>{(ur.positionsDecoded || 0).toLocaleString()}</td>
                      <td>{formatDateTime(ur.firstSeenAt)}</td>
                      <td>{formatDateTime(ur.lastSeenAt)}</td>
                      <td className="unknown-actions">
                        <button className="view-map-btn" onClick={() => handleAdoptClick(ur)} disabled={actionLoading}>
                          {adoptingId === ur.id ? 'Cancel' : 'Adopt'}
                        </button>
                        <button className="dismiss-btn" onClick={() => handleDismiss(ur.id)} disabled={actionLoading}>
                          Dismiss
                        </button>
                      </td>
                    </tr>
                    {adoptingId === ur.id && (
                      <tr key={`${ur.id}-adopt`} className="adopt-form-row">
                        <td colSpan="9">
                          <div className="adopt-form">
                            <div className="adopt-form-fields">
                              <label>
                                Owner Email
                                <input
                                  type="email"
                                  value={adoptForm.ownerEmail}
                                  onChange={e => setAdoptForm(f => ({ ...f, ownerEmail: e.target.value }))}
                                  placeholder="user@example.com"
                                  required
                                />
                              </label>
                              <label>
                                Receiver Name
                                <input
                                  type="text"
                                  value={adoptForm.receiverName}
                                  onChange={e => setAdoptForm(f => ({ ...f, receiverName: e.target.value }))}
                                  placeholder="My Receiver"
                                  required
                                />
                              </label>
                              <label>
                                Latitude
                                <input
                                  type="number"
                                  step="any"
                                  value={adoptForm.latitude}
                                  onChange={e => setAdoptForm(f => ({ ...f, latitude: e.target.value }))}
                                  placeholder="Estimated from positions"
                                />
                              </label>
                              <label>
                                Longitude
                                <input
                                  type="number"
                                  step="any"
                                  value={adoptForm.longitude}
                                  onChange={e => setAdoptForm(f => ({ ...f, longitude: e.target.value }))}
                                  placeholder="Estimated from positions"
                                />
                              </label>
                            </div>
                            <div className="adopt-form-actions">
                              {ur.estimatedLatitude != null && (
                                <span className="adopt-form-hint">
                                  Estimated: {ur.estimatedLatitude.toFixed(4)}, {ur.estimatedLongitude.toFixed(4)}
                                </span>
                              )}
                              <button
                                className="view-map-btn"
                                onClick={() => handleAdoptSubmit(ur.id)}
                                disabled={actionLoading || !adoptForm.ownerEmail.trim() || !adoptForm.receiverName.trim()}
                              >
                                {actionLoading ? 'Adopting...' : 'Confirm Adopt'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Connection Events Log — receiver-facing only. It was rendered
          unconditionally, so it also sat under Track Anomalies and every other
          tab, where it is noise. */}
      {(activeTab === 'users' || activeTab === 'unknown') && (
        <div className="receiver-event-log">
        <h3>Receiver Connection Events</h3>
        <div className="event-log-container">
          {allEvents.length === 0 ? (
            <div className="event-log-empty">No connection events yet. Events will appear here in real-time.</div>
          ) : (
            allEvents.map((event, idx) => (
              <div key={idx} className={`event-entry event-${event.eventType?.toLowerCase()}`}>
                <span className="event-time">{formatTime(event.timestamp)}</span>
                <span className={`event-type event-type-${event.eventType?.toLowerCase()}`}>
                  {getEventLabel(event.eventType)}
                </span>
                <span className="event-connection">{event.connectionId || '-'}</span>
                <span className="event-ip">{event.sourceIp || '-'}</span>
                <span className="event-receiver">{event.receiverName || event.receiverUuid || '-'}</span>
                <span className="event-message">
                  {event.message || '-'}
                  {event.reason && <span className="event-reason"> ({event.reason})</span>}
                </span>
              </div>
            ))
          )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
